import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';

const TABS = [
  { to: '/logbook', label: 'Entries' },
  { to: '/logbook/totals', label: 'Totals' },
  { to: '/logbook/currency', label: 'Currency' },
  { to: '/logbook/checks', label: 'Checks' },
  { to: '/logbook/export', label: 'Print & export' },
] as const;

/**
 * The layout for every `/logbook/*` route — the compliance line
 * (`NZ-LOGBOOK.md` §2.1) plus the destinations Phase 5, Phase 6 and the CAA
 * 1373 redesign between them add: the entry grid, totals, currency, the
 * formal checks, and print/export.
 *
 * `print:hidden` on the compliance line and the tab strip, not on the whole layout —
 * `/logbook/print` nests under this same layout (`PrintView` is its own
 * component, not a separate top-level route, so `@page` in `globals.css` only
 * has to be scoped by `@media print` once), and what should survive to paper
 * is the `<Outlet />`'s own content, not this page's on-screen chrome.
 */
function LogbookLayout() {
  const pathname = useLocation({ select: (location) => location.pathname });
  return (
    <div className="w-full space-y-4">
      <nav aria-label="Logbook" className="flex flex-wrap gap-1 print:hidden">
        {TABS.map((tab) => {
          /*
            Active state worked out here rather than by the router's
            `activeProps`: the printable view is Print & export's own page,
            at a route of its own, and it left no tab lit.
          */
          const active =
            tab.to === '/logbook'
              ? pathname === '/logbook' || pathname === '/logbook/'
              : pathname.startsWith(tab.to) ||
                (tab.to === '/logbook/export' && pathname.startsWith('/logbook/print'));
          return (
            <Link
              key={tab.to}
              to={tab.to}
              aria-current={active ? 'page' : undefined}
              className={`rounded-control px-3 py-1.5 text-sm transition-colors duration-150 ${
                active
                  ? 'bg-accent-soft text-foreground'
                  : 'text-muted-foreground hover:bg-surface-sunken hover:text-foreground'
              }`}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>
      <Outlet />
      {/*
        Said once, quietly, under every logbook page — rather than as a
        full-width banner above them, repeated again inside Currency and
        three times on the printable view.
      */}
      <p className="text-muted-foreground pt-4 text-xs print:hidden">
        The bound logbook remains the legal record.
      </p>
    </div>
  );
}

export const Route = createFileRoute('/logbook')({
  component: LogbookLayout,
});
